import * as actions from "./actions";
import * as types from "./types";

import AsyncStorage from "@react-native-async-storage/async-storage";

import { FileCleaner } from "utils";
import {
  apiCall,
  FEEDBACK_SUBMIT_URL,
  FEEDBACK_REVIEW_URL,
  FEEDBACK_UPDATE_URL,
} from "apis";
import { FeedbackSerializer, FeedbackMediaSerializer } from "serializers";

import {
  incrementSubmittedCount,
  setSubmittedCount,
} from "state/modules/taker";
import { persistState } from "state/modules/root";
import { startSurvey } from "state/modules/navigation";
import { setBenchmarkAchieved } from "state/modules/division";
import { discardLastResponseTimer } from "state/modules/response";
import { getSurveyQuestionnaire } from "screens/question/selectors";
import {
  STATUS,
  clearSurvey,
  setInviteId,
  setSurveyStatus,
  setQuestionnaire,
  updateSurveyScore,
  selectQuestionnaire,
} from "state/modules/survey";
import {
  DEFAULT_MESSAGES,
  LANGUAGE_CONFIGURATION_KEYS,
} from "consts/default-messages";

import { Navigator } from "router";

import { INVITES_STORE_ID } from "consts";

export const setFeedbackGroup = actions.setFeedbackGroup;

export const getFeedbackReview = () => async (dispatch, getState) => {
  const state = getState();
  const isReviewReceived = state.feedback.isReviewReceived;
  const { id } = state.organization;
  const feedback = await FeedbackSerializer(state);
  let feedbackOptions = feedback.feedback_options.map(
    fbOption => fbOption["option"],
  );
  feedbackOptions = feedbackOptions.join();
  const prevOptions = state.feedback.selected_fb_options;
  const sameOptionsSelected = feedbackOptions == prevOptions;

  if (!isReviewReceived || !sameOptionsSelected) {
    dispatch(actions.setSelectedFeedbackOptions(feedbackOptions));
    dispatch(actions.setFeedbackReview(""));
    dispatch(actions.setIsReviewReceived(false));
    dispatch(
      apiCall({
        type: types.GET_FEEDBACK_REVIEW,
        payload: {
          url: FEEDBACK_REVIEW_URL(id, feedbackOptions),
          data: {},
          onSuccess: response => d => {
            d(actions.setFeedbackReview(response));
            d(actions.setIsReviewReceived(true));
          },
          onError: () => d => {
            d(submitFeedback(true));
          },
          onDisconnected: () => d => {
            d(submitFeedback(true));
          },
        },
      }),
    );
  }
};

export const submitFeedback = (isComplete, noMedia) => async (
  dispatch,
  getState,
) => {
  const state = getState();
  const isStandardApp = !(state.survey.inviteId || state.survey.isAppEmbed);

  const questionnaire = getSurveyQuestionnaire(state);
  if (
    isStandardApp ||
    (questionnaire.config?.refresh_after_submission && !state.customer.id)
  ) {
    Navigator.jumpTo("thankyou");
    dispatch(startNextSurvey());
  }

  dispatch(updateSurveyScore());
  dispatch(discardLastResponseTimer());

  if (state.survey.isDemo) {
    return;
  }

  const feedback = await FeedbackSerializer(state, isComplete);
  const serializedMediaFeedback = await FeedbackMediaSerializer(
    feedback,
    noMedia,
  );
  serializedMediaFeedback["submitted_by"] = state.survey.submittedBy;

  const { id } = state.organization;

  if (state.survey.lastFeedbackId) {
    dispatch(
      apiCall({
        type: types.POST_FEEDBACK_UPDATE,
        payload: {
          url: FEEDBACK_UPDATE_URL(id, state.survey.lastFeedbackId),
          data: serializedMediaFeedback,
        },
      }),
    );
    return;
  }

  dispatch(
    apiCall({
      type: types.POST_FEEDBACK,
      payload: {
        url: FEEDBACK_SUBMIT_URL(id),
        data: serializedMediaFeedback,
        zip: serializedMediaFeedback._hasMedia,
        onSuccess: (feedback, response) => d => {
          d(onSuccess(feedback, response));
        },
        onError: () => d => d(onError(feedback)),
        onDisconnected: () => d => d(onDisconnected(feedback)),
        onUploadProgress: isStandardApp
          ? updatePercentUploaded(dispatch)
          : undefined,
      },
    }),
  );
};

const storeInviteId = async inviteId => {
  let submitted = await AsyncStorage.getItem(INVITES_STORE_ID);
  submitted = JSON.parse(submitted) || {};
  submitted[inviteId] = new Date().getTime();
  await AsyncStorage.setItem(INVITES_STORE_ID, JSON.stringify(submitted));
};

export const onSuccess = (feedback, response) => async (dispatch, getState) => {
  const { survey } = getState();
  if (response["status"] == false) {
    dispatch(actions.setFeedbackStatus(response));
  } else {
    dispatch(
      actions.setFeedbackStatus({
        status: true,
        message: "Feedback Subbmitted!",
      }),
    );
  }
  dispatch(setSurveyStatus(STATUS.SUBMITTED));
  if (survey.inviteId) {
    if (survey.preventInviteId) {
      await storeInviteId(survey.preventInviteId);
    }
    return;
  }
  dispatch(incrementSubmittedCount());
  dispatch(persistState());
  deleteSubmittedFiles(feedback);
};

export const onError = feedback => (dispatch, getState) => {
  const { organization } = getState();
  const language_configuration = organization.language_configuration;
  const message =
    (LANGUAGE_CONFIGURATION_KEYS.thankyou_screen_default_error_message in
      language_configuration &&
      language_configuration[
        LANGUAGE_CONFIGURATION_KEYS.thankyou_screen_default_error_message
      ]) ||
    DEFAULT_MESSAGES.thankyou_screen_default_error_message;
  dispatch(actions.setFeedbackStatus({ status: false, message: message }));
  dispatch(setSurveyStatus(STATUS.SUBMITTED));
  dispatch(actions.cacheFeedback(feedback));
  dispatch(persistState());
};

export const onDisconnected = feedback => dispatch => {
  dispatch(setSurveyStatus(STATUS.SUBMITTING));
  dispatch(actions.cacheFeedback(feedback));
  dispatch(persistState());
};

export const checkForNewDay = () => (dispatch, getState) => {
  const state = getState();
  if (!state.loading.rehydrated) {
    return;
  }

  const { date } = state.feedback;
  const today = new Date().toDateString();
  if (!date) {
    dispatch(actions.setDate(today));
    dispatch(persistState());
    return;
  }
  if (today !== date) {
    dispatch(startNewDay(today));
  }
};

export const startNewDay = newDay => (dispatch, getState) => {
  const { organization } = getState();
  if (organization.track_taker_session) {
    Navigator.jumpTo("session");
  }

  dispatch(actions.setDate(newDay));
  dispatch(setSubmittedCount(0));
  dispatch(setBenchmarkAchieved(0));
  dispatch(persistState());
};

export const retryFeedbackSubmissions = () => async (dispatch, getState) => {
  const state = getState();
  const { cached } = state.feedback;
  const { id } = state.organization;

  if (!state.network.isConnected) {
    return;
  }

  for (const feedback of cached) {
    const serializedMediaFeedback = await FeedbackMediaSerializer(feedback);
    dispatch(
      apiCall({
        type: types.POST_FEEDBACK,
        payload: {
          url: FEEDBACK_SUBMIT_URL(id),
          data: serializedMediaFeedback,
          onSuccess: () => d => d(onRetrySucces(feedback)),
        },
      }),
    );
  }
};

export const onRetrySucces = feedback => (dispatch, getState) => {
  const { date } = getState().feedback;
  const createdAt = new Date(feedback.created_at).toDateString();
  if (date === createdAt) {
    dispatch(incrementSubmittedCount());
  }
  dispatch(actions.removeCachedFeedback(feedback));
  dispatch(persistState());
  deleteSubmittedFiles(feedback);
};

export const loadFeedbacks = () => async dispatch => {
  const rawFeedbacks = await AsyncStorage.getItem("feedbacks");
  const feedbacks = JSON.parse(rawFeedbacks) || [];
  if (feedbacks.length) {
    dispatch(actions.loadFeedbacks(feedbacks));
  }
  AsyncStorage.removeItem("feedbacks");
};

export const saveFeedbacks = () => async (_, getState) => {
  const { cached } = getState().feedback;
  if (cached.length) {
    await AsyncStorage.setItem("feedbacks", JSON.stringify(cached));
  }
};

export const tryAgainQuiz = () => (dispatch, getState) => {
  const { survey, questionnaires } = getState();
  const questionnaire = questionnaires.find(
    q => q.id === survey.questionnaireId,
  );

  const startAgain = () => {
    dispatch(setQuestionnaire(questionnaire));
    dispatch(setInviteId(survey.inviteId));
    Navigator.jumpTo("start");
  };
  const cleared = dispatch(clearSurvey());
  cleared ? cleared.then(() => startAgain()) : startAgain();
};

const startNextSurvey = () => (dispatch, getState) => {
  const state = getState();
  const onTimeOut = state.survey.forGo
    ? startGoSurvey
    : state.survey.inviteId
    ? refreshSurvey
    : state.survey.isDemo
    ? restartDemo
    : startStandardSurvey;
  setTimeout(() => onTimeOut(dispatch), 4000);
};

const startStandardSurvey = dispatch => {
  dispatch(clearSurvey());
  Navigator.jumpTo("start");
};

const refreshSurvey = () => {
  window.location.reload();
};

const restartDemo = dispatch => {
  dispatch(clearSurvey());
  dispatch(setSurveyStatus(STATUS.PENDING));
};

const startGoSurvey = dispatch => {
  const cleared = dispatch(clearSurvey());
  if (cleared) {
    cleared.then(() => {
      dispatch(selectQuestionnaire());
      dispatch(startSurvey());
    });
  } else {
    dispatch(selectQuestionnaire());
    dispatch(startSurvey());
  }
};

const deleteSubmittedFiles = feedback => {
  const submittedFilesURIs = feedback.feedback_options
    .filter(option => option.files?.length)
    .map(option => option.files.map(file => file.uri))
    .flat();
  FileCleaner(submittedFilesURIs);
};

const updatePercentUploaded = dispatch => progressEvent => {
  const percentCompleted = Math.floor(
    (progressEvent.loaded * 100) / progressEvent.total,
  );
  dispatch(actions.updatePercentUploaded(percentCompleted));
};
